import { useEffect, useState } from "react";
import { WarningIcon as Warning, CircleNotchIcon as Spinner } from "@phosphor-icons/react";
import { BINDING_OPERATIONS, MANUAL_WARNING, manualBindingPayload, operationLabel, OPERATION_META, validateManualBinding } from "@/lib/screenBindings";

const EMPTY = { program_library: "", program_name: "", display_file: "", record_format: "", operation: "exfmt", confidence: "medium", manual_note: "" };
const inputCls = "ml-mono text-xs w-full px-2 py-1.5 border border-[var(--ml-border)] bg-white";

/** Iter 48 — create / edit a consultant-supplied screen format binding. Inline panel, no LLM, no invented proof. */
export default function ManualBindingForm({ initial, onSubmit, onCancel, busy, serverError }) {
  const [form, setForm] = useState(() => ({ ...EMPTY, ...(initial || {}) }));
  const [touched, setTouched] = useState(false);
  useEffect(() => { setForm({ ...EMPTY, ...(initial || {}) }); setTouched(false); }, [initial]);
  const errors = validateManualBinding(form);
  const editing = Boolean(initial?.id);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const field = (label, key, testid, { upper = true, placeholder = "", hint = "" } = {}) => (
    <label className="block text-xs">
      <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">{label}</span>
      <input type="text" value={form[key]} onChange={set(key)} placeholder={placeholder} maxLength={10} data-testid={testid}
        className={`${inputCls} mt-1 ${upper ? "uppercase" : ""} ${touched && errors[key] ? "border-[#da1e28]" : ""}`} />
      {touched && errors[key] ? <span className="text-[11px] text-[#da1e28]" data-testid={`${testid}-error`}>{errors[key]}</span>
        : hint ? <span className="text-[11px] text-[var(--ml-text-secondary)]">{hint}</span> : null}
    </label>
  );
  const submit = (e) => {
    e.preventDefault();
    setTouched(true);
    if (Object.keys(errors).length) return;
    onSubmit(manualBindingPayload(form));
  };
  return (
    <form onSubmit={submit} className="ml-card p-4 space-y-3 border-[#ffb3b8]" data-testid="sb-manual-form" data-mode={editing ? "edit" : "create"}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">{editing ? `Edit manual binding #${initial.id}` : "Add manual binding"}</div>
          <div className="text-sm font-semibold mt-0.5">"This RPG program uses this DSPF record format."</div>
        </div>
        <button type="button" onClick={onCancel} className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] hover:underline" data-testid="sb-manual-cancel">Cancel</button>
      </div>
      <p className="text-xs px-3 py-2 flex items-start gap-2 bg-[#fff1f1] text-[#a2191f]" data-testid="sb-manual-warning">
        <Warning size={14} weight="bold" className="shrink-0 mt-0.5" />
        <span>{MANUAL_WARNING} No source line or excerpt is invented; the binding survives rebuilds and is labelled Manual everywhere.</span>
      </p>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {field("Program library", "program_library", "sb-manual-program-library", { placeholder: "optional", hint: "optional" })}
        {field("Program", "program_name", "sb-manual-program")}
        {field("Display file", "display_file", "sb-manual-display-file", { hint: "parsed DSPF where possible" })}
        {field("Record format", "record_format", "sb-manual-record-format")}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <label className="block text-xs">
          <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Operation</span>
          <select value={form.operation} onChange={set("operation")} className={`${inputCls} mt-1`} data-testid="sb-manual-operation" title={(OPERATION_META[form.operation] || {}).hint}>
            {BINDING_OPERATIONS.map((o) => <option key={o} value={o}>{operationLabel(o)}</option>)}
          </select>
        </label>
        <label className="block text-xs">
          <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Consultant confidence</span>
          <select value={form.confidence} onChange={set("confidence")} className={`${inputCls} mt-1`} data-testid="sb-manual-confidence">
            {["high", "medium", "low"].map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </label>
        <label className="block text-xs md:col-span-2">
          <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">Manual note — why does this program use this format? (required)</span>
          <textarea value={form.manual_note} onChange={set("manual_note")} rows={2} data-testid="sb-manual-note"
            placeholder="e.g. Client SME confirmed the program is compiled over this display file; the format was renamed in source."
            className={`${inputCls} mt-1 ${touched && errors.manual_note ? "border-[#da1e28]" : ""}`} />
          {touched && errors.manual_note && <span className="text-[11px] text-[#da1e28]" data-testid="sb-manual-note-error">{errors.manual_note}</span>}
        </label>
      </div>
      {serverError && <p className="text-xs text-[#a2191f]" data-testid="sb-manual-server-error">{serverError}</p>}
      <div className="flex items-center gap-3">
        <button type="submit" disabled={busy} className="ml-btn-primary flex items-center gap-1.5" data-testid="sb-manual-submit">
          {busy && <Spinner size={14} className="animate-spin" />}
          {editing ? "Save manual binding" : "Add manual binding"}
        </button>
        <span className="text-[11px] text-[var(--ml-text-secondary)]">Requires owner / editor / admin. Field references and the walkthrough are rebuilt immediately.</span>
      </div>
    </form>
  );
}
